import React from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';

export type TrendVariant = 'chip' | 'icon';

export type TrendAdornmentProps = {
  variant: TrendVariant;
  pct: number | null;
};

type Direction = 'up' | 'down';

const Chip = styled.span<{ direction: Direction }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  padding: 0 3px;
  border-radius: 7px;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  white-space: nowrap;

  &.direction-up {
    color: var(--success-dark, #05b01c);
    background: var(--success-light, #f0f8f1);
  }

  &.direction-down {
    color: var(--error-dark, #b60000);
    background: var(--error-light, #fff2f2);
  }
`;

const IconWrap = styled.span<{ direction: Direction }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;

  &.direction-up {
    color: var(--success-dark, #05b01c);
  }

  &.direction-down {
    color: var(--error-dark, #b60000);
  }
`;

const ICON_NAMES: Record<Direction, string> = {
  up: 'trending_up',
  down: 'trending_down',
};

function roundPct(pct: number): number {
  return Number(pct.toFixed(1)) + 0;
}

export function formatTrendPct(pct: number): string {
  const rounded = roundPct(pct);
  const sign = rounded >= 0 ? '+' : '';
  return `${sign}${rounded.toFixed(1)}%`;
}

function trendDirection(pct: number): Direction {
  return roundPct(pct) >= 0 ? 'up' : 'down';
}

const TrendAdornment = ({ variant, pct }: TrendAdornmentProps) => {
  if (pct == null) {
    return null;
  }

  const direction = trendDirection(pct);

  if (variant === 'chip') {
    return (
      <Chip
        data-testid="trend-chip"
        data-direction={direction}
        direction={direction}
      >
        {formatTrendPct(pct)}
      </Chip>
    );
  }

  return (
    <IconWrap
      data-testid="trend-icon"
      data-direction={direction}
      direction={direction}
    >
      <Icon
        name={ICON_NAMES[direction]}
        size={16}
        role="img"
        aria-label={`Trend: ${direction}`}
      />
    </IconWrap>
  );
};

export default TrendAdornment;
